import { useCallback, useEffect, useMemo } from 'react';

import { Form, Input, notification, Select } from 'antd';

import {
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_RULES,
  MODAL_WIDTHS,
  PLACEHOLDER_MESSAGES,
  VALIDATION_RULES,
} from '@/shared/lib/constants';
import { transformOwnersToSelectOptions } from '@/shared/lib/utils';
import type { EntityReferenceBasic, JsonPatchOperation } from '@/shared/types';
import { BaseModal } from '@/shared/ui/BaseModal';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { normalizeGlossaryName } from '@/entities/glossary';
import type { Owner, Reviewer } from '@/entities/glossary';
import { useUserOwners } from '@/entities/owner';

import { useCreateGlossaryCategory, useUpdateGlossaryCategory } from '@/features/glossary/model';
import styles from '@/features/glossary/ui/_internal/GlossaryFormModal.module.css';

type FormValues = {
  name: string;
  displayName?: string;
  description: string;
  owners?: string[];
  reviewers?: string[];
};

type CategoryData = {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  owners?: Owner[];
  reviewers?: Reviewer[];
};

type GlossaryCategoryFormModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  categoryData?: CategoryData;
  onClose: () => void;
  onSuccess?: () => void;
};

export const GlossaryCategoryFormModal = ({
  open,
  mode,
  categoryData,
  onClose,
  onSuccess,
}: GlossaryCategoryFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const createMutation = useCreateGlossaryCategory();
  const updateMutation = useUpdateGlossaryCategory();
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const { data: usersData } = useUserOwners();

  const isEditMode = mode === 'edit';
  const isLoading = createMutation.isPending || updateMutation.isPending;
  const modalTitle = isEditMode ? `용어집 수정 (${categoryData?.displayName || categoryData?.name})` : '용어집 추가';

  const userOptions = useMemo(() => transformOwnersToSelectOptions(usersData), [usersData]);

  useEffect(() => {
    if (open) {
      if (isEditMode && categoryData) {
        form.setFieldsValue({
          name: categoryData.name,
          displayName: categoryData.displayName,
          description: categoryData.description,
          owners: categoryData.owners?.map((o) => o.id) || [],
          reviewers: categoryData.reviewers?.map((r) => r.id) || [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, isEditMode, categoryData, form]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const generatePatchOperations = useCallback(
    (values: FormValues): JsonPatchOperation[] => {
      if (!categoryData) return [];

      const operations: JsonPatchOperation[] = [];

      if (values.displayName !== categoryData.displayName) {
        if (values.displayName) {
          operations.push({
            op: categoryData.displayName ? 'replace' : 'add',
            path: '/displayName',
            value: values.displayName,
          });
        } else if (categoryData.displayName) {
          operations.push({ op: 'remove', path: '/displayName' });
        }
      }

      if (values.description !== categoryData.description) {
        if (values.description) {
          operations.push({
            op: categoryData.description ? 'replace' : 'add',
            path: '/description',
            value: values.description,
          });
        } else if (categoryData.description) {
          operations.push({ op: 'remove', path: '/description' });
        }
      }

      const currentOwnerIds = categoryData.owners?.map((o) => o.id) || [];
      const newOwnerIds = values.owners || [];
      const ownersChanged =
        currentOwnerIds.length !== newOwnerIds.length || !currentOwnerIds.every((id) => newOwnerIds.includes(id));
      if (ownersChanged) {
        operations.push({
          op: currentOwnerIds.length > 0 ? 'replace' : 'add',
          path: '/owners',
          value: newOwnerIds.map((id) => ({ id, type: 'user' })),
        });
      }

      const currentReviewerIds = categoryData.reviewers?.map((r) => r.id) || [];
      const newReviewerIds = values.reviewers || [];
      const reviewersChanged =
        currentReviewerIds.length !== newReviewerIds.length ||
        !currentReviewerIds.every((id) => newReviewerIds.includes(id));
      if (reviewersChanged) {
        operations.push({
          op: currentReviewerIds.length > 0 ? 'replace' : 'add',
          path: '/reviewers',
          value: newReviewerIds.map((id) => ({ id, type: 'user' })),
        });
      }

      return operations;
    },
    [categoryData]
  );

  const transformFormValuesToRequest = useCallback((values: FormValues) => {
    const normalizedName = normalizeGlossaryName(values.name);
    const owners: EntityReferenceBasic[] = (values.owners || []).map((id) => ({
      id,
      type: 'user',
    }));

    const reviewers: EntityReferenceBasic[] = (values.reviewers || []).map((id) => ({
      id,
      type: 'user',
    }));

    return {
      name: normalizedName,
      displayName: values.displayName || undefined,
      description: values.description || undefined,
      owners,
      reviewers,
    };
  }, []);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (isEditMode && categoryData) {
          const operations = generatePatchOperations(values);
          if (operations.length === 0) {
            notification.info({
              message: '변경 사항 없음',
              description: '수정된 내용이 없습니다.',
              placement: 'bottomLeft',
              duration: 3,
            });
            return;
          }
          await updateMutation.mutateAsync({
            glossaryId: categoryData.id,
            operations,
          });
        } else {
          const request = transformFormValuesToRequest(values);
          await createMutation.mutateAsync(request);
        }
        handleClose();
        onSuccess?.();
      } catch (error) {
        console.error('Failed to submit glossary category form:', error);
      }
    },
    [
      isEditMode,
      categoryData,
      generatePatchOperations,
      updateMutation,
      createMutation,
      transformFormValuesToRequest,
      handleClose,
      onSuccess,
    ]
  );

  return (
    <BaseModal
      title={modalTitle}
      open={open}
      onClose={handleClose}
      width={MODAL_WIDTHS.STANDARD_FORM}
      onOk={() => form.submit()}
      okText={isEditMode ? '수정' : '생성'}
      confirmLoading={isLoading}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
        <Form.Item
          name="name"
          label={FORM_LABELS.NAME}
          rules={[...FORM_RULES.REQUIRED_NAME, ...FORM_RULES.GLOSSARY_CATEGORY_NAME]}
          validateFirst>
          <Input
            placeholder={FORM_PLACEHOLDERS.NAME}
            disabled={isEditMode}
            maxLength={VALIDATION_RULES.PROJECT_NAME_MAX_LENGTH}
            allowClear
          />
        </Form.Item>

        <Form.Item name="displayName" label={FORM_LABELS.DISPLAY_NAME}>
          <Input
            placeholder={FORM_PLACEHOLDERS.DISPLAY_NAME}
            maxLength={VALIDATION_RULES.DISPLAY_NAME_MAX_LENGTH}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={FORM_LABELS.DESCRIPTION}
          rules={[...FORM_RULES.REQUIRED_DESCRIPTION, ...FORM_RULES.GLOSSARY_CATEGORY_DESCRIPTION]}
          validateTrigger={['onSubmit']}
          validateFirst>
          <DescriptionTextArea
            placeholder={FORM_PLACEHOLDERS.DESCRIPTION}
            showHelper
            maxLength={VALIDATION_RULES.GLOSSARY_DESCRIPTION_MAX_LENGTH}
          />
        </Form.Item>

        {showOwners && (
          <Form.Item name="owners" label={FORM_LABELS.OWNERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}

        {showOwners && (
          <Form.Item name="reviewers" label={FORM_LABELS.REVIEWERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}
      </Form>
    </BaseModal>
  );
};
